import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useUserApi } from "src/_common/hooks/actions/user/appUserApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { APPLICATION_GLOBAL_SLUG } from "src/_config";
import InputTextField from "src/components/common/Form/InputTextField";
import AuthModalHeader from "./AuthModalHeader";
import AuthModalFooter from "./AuthModalFooter";

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBackToLogin: () => void;
  onForgotPasswordSuccess?: (resetToken?: string) => void; // Callback to trigger reset password modal
}

interface ForgotPasswordFormValues {
  email: string;
}

const forgotPasswordSchema = yup.object().shape({
  email: yup.string().email("Please enter a valid email").required("Email is required"),
});

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({
  isOpen,
  onClose,
  onBackToLogin,
  onForgotPasswordSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  // Hooks (same as forgotpassword.tsx)
  const userApi = useUserApi();
  const toast = useToaster();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ForgotPasswordFormValues>({
    resolver: yupResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = (data: ForgotPasswordFormValues) => {
    setIsLoading(true);

    // Prepare API data (same as forgotpassword.tsx)
    const apiData = {
      ...data,
      game_slug: APPLICATION_GLOBAL_SLUG,
    };

    userApi.callForgotPassword(
      apiData,
      (message: string, resp: any) => {
        setIsLoading(false);
        if (resp) {
          toast.success(message);
          reset();
          // Close forgot password modal and trigger reset password modal
          onClose();
          // Trigger the reset password modal with any token from response
          if (onForgotPasswordSuccess) {
            onForgotPasswordSuccess(resp.token || "");
          }
        } else {
          toast.error(message);
        }
      },
      (message: string) => {
        setIsLoading(false);
        toast.error(message);
      }
    );
  };

  const handleClose = () => {
    reset();
    setIsLoading(false);
    onClose();
  };

  const handleBackToLogin = () => {
    reset();
    setIsLoading(false);
    onBackToLogin();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal fade show" style={{ display: "block" }} tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content signin-modal">
            <div className="modal-header border-0">
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={handleClose}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body text-center">
              {/* Header with Logo */}
              <AuthModalHeader />

              {/* Lock Icon */}
              <div className="signin-icon mb-3">
                <div className="lock-icon">
                  <i className="fas fa-lock"></i>
                </div>
              </div>

              {/* Title */}
              <h2 className="signin-title mb-2">Reset Password</h2>
              <p className="signin-subtitle mb-4">Enter your email and we&apos;ll send you reset instructions.</p>

              <form onSubmit={handleSubmit(onSubmit)}>
                {/* Email Input */}
                <div className="email-input-container mb-3">
                  <div className="input-group">
                    <span className="input-group-text email-modal-prefix">
                      <i className="fas fa-envelope"></i>
                    </span>
                    <InputTextField
                      fieldName="email"
                      control={control}
                      errors={errors}
                      placeholder="Enter your email address"
                      fieldType="email"
                    />
                  </div>
                </div>

                {/* Instructions */}
                <div className="reset-instructions mb-4">
                  <p className="instructions-text">
                    Check your email for reset instructions. Check spam folder if not received.
                  </p>
                </div>

                {/* Send Reset Link Button */}
                <button type="submit" className="btn btn-success w-100 mb-3 email-modal-login-btn" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Sending...
                    </>
                  ) : (
                    "Send Reset Link"
                  )}
                </button>
              </form>

              {/* Back to Login Link */}
              <div className="back-to-login-container mb-3">
                <button className="btn btn-link back-to-login-btn p-0" onClick={handleBackToLogin}>
                  <i className="fas fa-arrow-left me-2"></i>
                  Back to Login
                </button>
              </div>

              {/* Need More Help */}
              <div className="need-help-container">
                <span className="need-help-text">Need more help?</span>
              </div>

              {/* FAQ & Help Center */}
              <div className="help-center-container mt-3">
                <a
                  href="/cms/help-center"
                  className="btn btn-link help-center-btn p-0"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fas fa-question-circle me-2"></i>
                  FAQ & Help Center
                </a>
              </div>
              {/* Footer Links */}
              <AuthModalFooter />
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
};

export default ForgotPasswordModal;
